/**
 * YunExpressLocationSettings — the selected location's YunExpress shipping
 * product and "buy label on order scan" switch. Rendered under the shipping
 * provider picker in Locations & Fulfilment > Shipping & invoicing.
 *
 * Shipping product is REQUIRED: without one YunExpress cannot buy a label, and
 * the backend refuses to turn labels-at-scan on without it
 * (`YUNEXPRESS_PRODUCT_REQUIRED`). It is ONE labelled, searchable control (the
 * shared `PrintSizeCodeCombobox` in single-value mode) that lists the
 * account's products as `CODE · name` and always accepts a typed code, so a
 * slow, empty or failed product list never blocks the admin. Codes keep their
 * case (`preserveCase`) and may be up to 50 characters, YunExpress's limit.
 *
 * The switch can be turned ON only once a product is set, and always stays
 * clearable: a switch that is on with no product (saved before the rule) can
 * still be turned off.
 *
 * Saving goes through the Console's sticky save bar only.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { readCauseCode } from '@/lib/trpc-error-utils';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { PrintSizeCodeCombobox } from './PrintSizeCodeCombobox';
import { useDirtyRegistration } from './console/SettingsDirtyContext';

/** YunExpress `product_code` is a 1 to 50 character string. */
const YUNEXPRESS_PRODUCT_CODE_MAX = 50;
const isYunExpressProductCode = (code: string): boolean =>
  code.length > 0 && code.length <= YUNEXPRESS_PRODUCT_CODE_MAX;

export function YunExpressLocationSettings({ locationId }: { locationId: string }) {
  const { t } = useTranslation('settings');
  const locations = trpc.productionSettings.getLocationSettings.useQuery();
  const location = locations.data?.find((loc) => loc.id === locationId);
  const productsEnabled = !!location?.shipsYunexpress;
  const methods = trpc.productionSettings.getYunExpressShippingMethods.useQuery(
    { locationId },
    { enabled: productsEnabled, retry: false },
  );
  const credentials = trpc.productionSettings.getYunExpressSettings.useQuery();
  const update = trpc.productionSettings.setLocationYunExpressSettings.useMutation();
  const [methodDraft, setMethodDraft] = useState<string | null>(null);
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null);
  const shippingMethod = methodDraft ?? location?.yunexpressShippingMethod ?? '';
  const prefetchEnabled = enabledDraft ?? location?.yunexpressLabelPrefetchEnabled ?? false;
  const hasProduct = shippingMethod.trim().length > 0;
  const dirty = methodDraft !== null || enabledDraft !== null;

  const discard = () => {
    setMethodDraft(null);
    setEnabledDraft(null);
  };
  const save = async () => {
    if (prefetchEnabled && !hasProduct) {
      const message = t('production.yunexpress.location.prefetchNeedsProduct');
      toast.error(message);
      throw new Error(message);
    }
    try {
      await update.mutateAsync({ locationId, shippingMethod: shippingMethod.trim(), prefetchEnabled });
    } catch (error) {
      toast.error(
        readCauseCode(error) === 'YUNEXPRESS_PRODUCT_REQUIRED'
          ? t('production.yunexpress.location.prefetchNeedsProduct')
          : t('production.yunexpress.location.saveError', {
              message: error instanceof Error ? error.message : String(error),
            }),
      );
      throw error;
    }
    await locations.refetch();
    discard();
  };
  useDirtyRegistration(
    `yunexpress-location-${locationId}`,
    dirty ? { label: t('production.yunexpress.location.dirtyLabel'), save, discard } : null,
  );

  if (!location || (!location.shipsYunexpress && !location.yunexpressLabelPrefetchEnabled && !location.yunexpressShippingMethod)) {
    return null;
  }

  const products = methods.data?.methods ?? [];
  const options = products.map((product) => ({ code: product.code, label: product.name || product.code }));
  const selectedName = products.find((product) => product.code === shippingMethod)?.name;
  const loadingProducts = productsEnabled && methods.isFetching && products.length === 0;

  // Say what the list is doing, and that typing a code always works.
  const productsHint = !productsEnabled
    ? t('production.yunexpress.location.productsOff')
    : loadingProducts
      ? t('production.yunexpress.location.productsLoading')
      : credentials.data?.hasCredentials === false
        ? t('production.yunexpress.location.productsNeedCredentials')
        : methods.isError || methods.data?.error
          ? t('production.yunexpress.location.productsUnavailable')
          : products.length === 0
            ? t('production.yunexpress.location.productsEmpty')
            : t('production.yunexpress.location.productsReady');

  // ON needs a product; OFF is always reachable.
  const switchLocked = !prefetchEnabled && !hasProduct;
  const productId = `yunexpress-product-${locationId}`;
  const productHintId = `${productId}-hint`;
  const productRequiredId = `${productId}-required`;
  const switchId = `yunexpress-prefetch-${locationId}`;
  const switchHelpId = `${switchId}-help`;
  const switchReasonId = `${switchId}-reason`;
  const timezone = location.timezone || 'Asia/Shanghai';

  return (
    <div className="space-y-4 border-t pt-4">
      <div>
        <h3 className="text-sm font-medium">{t('production.yunexpress.location.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('production.yunexpress.location.description')}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor={productId}>
          {t('production.yunexpress.location.product')}
          <span className="ml-1 text-destructive" aria-hidden="true">
            *
          </span>
        </Label>
        <PrintSizeCodeCombobox
          id={productId}
          ariaRequired
          ariaDescribedBy={hasProduct ? productHintId : `${productRequiredId} ${productHintId}`}
          value={hasProduct ? { code: shippingMethod, label: selectedName } : null}
          options={options}
          status={products.length > 0 ? 'loaded' : 'empty'}
          preserveCase
          isValidCode={isYunExpressProductCode}
          onSelect={(code) => {
            setMethodDraft(code);
            return true;
          }}
          placeholder={t('production.yunexpress.location.productPlaceholder')}
          ariaLabel={t('production.yunexpress.location.product')}
          addButtonLabel={t('production.yunexpress.location.useCodeButton')}
          createLabel={(code) => t('production.yunexpress.location.useCode', { code })}
          emptyLabel={
            loadingProducts
              ? t('production.yunexpress.location.productsLoadingShort')
              : t('production.yunexpress.location.noMatches')
          }
          className={cn('sm:w-full sm:max-w-md', !hasProduct && 'border-amber-300 ring-1 ring-amber-100/50')}
        />
        <p id={productHintId} className="text-xs text-muted-foreground" role="status">
          {productsHint}
        </p>
        {/* The ONE amber warning for a missing product, at the control that
            fixes it. The provider row above does not repeat it
            (`yunexpressProductFieldBelow`), and the switch lines below are
            muted explanations, not second warnings. */}
        {!hasProduct && (
          <p id={productRequiredId} className="text-xs text-amber-700">
            {t('production.yunexpress.location.productRequired')}
          </p>
        )}
        {productsEnabled && (
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            disabled={methods.isFetching}
            onClick={() => void methods.refetch()}
          >
            {t('production.yunexpress.location.refresh')}
          </Button>
        )}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor={switchId}>{t('production.yunexpress.location.prefetch')}</Label>
          <p id={switchHelpId} className="text-xs text-muted-foreground">
            {t('production.yunexpress.location.prefetchHelp', { timezone })}
          </p>
          {switchLocked && (
            <p id={switchReasonId} className="text-xs text-muted-foreground">
              {t('production.yunexpress.location.prefetchNeedsProduct')}
            </p>
          )}
          {prefetchEnabled && !hasProduct && (
            <p className="text-xs text-muted-foreground" role="status">
              {t('production.yunexpress.location.prefetchOnWithoutProduct')}
            </p>
          )}
        </div>
        <Switch
          id={switchId}
          checked={prefetchEnabled}
          onCheckedChange={setEnabledDraft}
          disabled={switchLocked}
          aria-describedby={switchLocked ? `${switchHelpId} ${switchReasonId}` : switchHelpId}
        />
      </div>
    </div>
  );
}
